import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from 'recharts';
import { donationsAPI, articlesAPI, usersAPI, inboxAPI, programsAPI } from '../../services/api';
import { StatCard, Badge, Spinner } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color:p.color, fontWeight:600 }}>{p.name}: {p.value?.toLocaleString()}</div>
      ))}
    </div>
  );
};

const CAN = {
  seeDonations:   (r) => ['super_admin','platform_admin'].includes(r),
  seeUsers:       (r) => ['super_admin','platform_admin'].includes(r),
  seePayments:    (r) => ['super_admin','platform_admin'].includes(r),
  seeEnrollments: (r) => ['super_admin','platform_admin','content_manager','trainer'].includes(r),
  seeContent:     (r) => ['super_admin','platform_admin','content_manager','editor','contributor'].includes(r),
  seeInbox:       (r) => ['super_admin','platform_admin','content_manager'].includes(r),
  seePrograms:    (r) => ['super_admin','platform_admin','content_manager','trainer'].includes(r),
};

export default function DashboardPage() {
  const { user } = useAuth();
  const role     = user?.role || 'contributor';

  const [loading, setLoading]         = useState(true);
  const [donations, setDonations]     = useState(null);
  const [articles, setArticles]       = useState([]);
  const [applications, setApps]       = useState([]);
  const [messages, setMessages]       = useState([]);
  const [userCount, setUserCount]     = useState(null);
  const [programs, setPrograms]       = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments]       = useState(null);
  const [monthlyData, setMonthly]     = useState([]);

  useEffect(() => {
    const fetches = [];

    if (CAN.seeDonations(role))
      fetches.push(donationsAPI.summary().then(r => setDonations(r.data.data)).catch(() => {}));

    if (CAN.seeContent(role))
      fetches.push(articlesAPI.list({ limit:5 }).then(r => setArticles(r.data.data || [])).catch(() => {}));

    if (CAN.seeInbox(role)) {
      fetches.push(
        inboxAPI.applications({ limit:5 }).then(r => setApps(r.data.data || [])).catch(() => {}),
        inboxAPI.messages({ limit:5 }).then(r => setMessages(r.data.data || [])).catch(() => {})
      );
    }

    if (CAN.seeUsers(role))
      fetches.push(usersAPI.list({ limit:1 }).then(r => setUserCount(r.data.pagination?.total)).catch(() => {}));

    if (CAN.seePrograms(role)) {
      fetches.push(
        programsAPI.list({ limit:50 }).then(r => setPrograms(r.data.data || [])).catch(() => {}),
        api.get('/admin/enrollments', { params:{ limit:5 } }).then(r => setEnrollments(r.data.data || [])).catch(() => {})
      );
    }

    if (CAN.seePayments(role)) {
      fetches.push(
        api.get('/admin/transactions/summary').then(r => setPayments(r.data.data)).catch(() => {})
      );
    }

    // Monthly enrollment chart
    api.get('/admin/enrollments', { params:{ limit:200 } }).then(r => {
      const rows = r.data.data || [];
      const months = {};
      rows.forEach(e => {
        const m = new Date(e.created_at).toLocaleDateString('en-RW', { month:'short' });
        months[m] = months[m] || { month:m, enrollments:0 };
        months[m].enrollments++;
      });
      setMonthly(Object.values(months).slice(-7));
    }).catch(() => {});

    Promise.allSettled(fetches).finally(() => setLoading(false));
  }, [role]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300 }}>
      <Spinner size={32}/>
    </div>
  );

  const pendingApps     = applications.filter(a => a.status === 'pending').length;
  const pendingMessages = messages.filter(m => m.status === 'new' || m.status === 'pending').length;
  const activePrograms  = programs.filter(p => p.status === 'ongoing' || p.status === 'active').length;
  const draftArticles   = articles.filter(a => a.status === 'draft').length;

  const revenueChartData = payments ? [
    { flow:'Programs',  amount: Number(payments.program_revenue  || 0) },
    { flow:'Services',  amount: Number(payments.service_revenue  || 0) },
    { flow:'Donations', amount: Number(payments.donation_revenue || 0) },
    { flow:'MoMo',      amount: Number(payments.momo_revenue     || 0) },
    { flow:'Stripe',    amount: Number(payments.stripe_revenue   || 0) },
  ].filter(d => d.amount > 0) : [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Banner */}
      <div style={{
        background:'linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%)',
        border:'1px solid var(--border)', borderLeft:'3px solid var(--accent)',
        borderRadius:'var(--radius-lg)', padding:'20px 24px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:700, marginBottom:4 }}>
            Welcome back, {user?.name?.split(' ')[0] || 'Admin'} 👋
          </div>
          <div style={{ fontSize:13, color:'var(--text-muted)' }}>
            MilleHills Ecosystem Console · <span style={{ textTransform:'capitalize', color:'var(--accent)' }}>{role?.replace('_',' ')}</span>
          </div>
        </div>
        <div style={{ fontSize:11, color:'var(--text-muted)', textAlign:'right', lineHeight:2 }}>
          <div style={{ color:'var(--green)', fontWeight:600 }}>● System Operational</div>
          <div>{new Date().toLocaleDateString('en-RW', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</div>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16 }}>

        {CAN.seeDonations(role) && <>
          <StatCard icon="◎" label="Total Raised"
            value={donations?.total_amount ? `RWF ${Number(donations.total_amount).toLocaleString()}` : '—'}
            delta={`${donations?.total_donations ?? 0} donations`} color="var(--green)"/>
          <StatCard icon="◈" label="Unique Donors"
            value={donations?.unique_donors ?? '—'}
            delta={`${donations?.completed_donations ?? 0} completed`} color="var(--accent)"/>
        </>}

        {CAN.seeUsers(role) && (
          <StatCard icon="◯" label="System Users"
            value={userCount ?? '—'}
            delta="Registered accounts" color="var(--blue)"/>
        )}

        {CAN.seePrograms(role) && <>
          <StatCard icon="◐" label="Active Programs"
            value={activePrograms}
            delta={`${programs.length} total`} color="var(--blue)"/>
          <StatCard icon="◉" label="Enrollments"
            value={enrollments.length}
            delta="Recent applicants" color="var(--amber)"/>
        </>}

        {CAN.seeInbox(role) && <>
          <StatCard icon="◻" label="Pending Applications"
            value={pendingApps}
            delta="Awaiting review" color="var(--amber)"/>
          <StatCard icon="▣" label="Unread Messages"
            value={pendingMessages}
            delta="Contact inbox" color="var(--blue)"/>
        </>}

        {CAN.seeContent(role) && (
          <StatCard icon="▤" label="Draft Articles"
            value={draftArticles}
            delta={`${articles.length} recent total`} color="var(--text-muted)"/>
        )}

        {CAN.seePayments(role) && payments && (
          <StatCard icon="◆" label="Revenue"
            value={payments.total_revenue ? `RWF ${Number(payments.total_revenue).toLocaleString()}` : '—'}
            delta={`${payments.paid_count ?? 0} paid · ${payments.pending_count ?? 0} pending`} color="var(--green)"/>
        )}
      </div>

      {/* Charts */}
      {(CAN.seePrograms(role) || CAN.seePayments(role)) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

          {CAN.seePrograms(role) && (
            <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'20px 24px' }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:14 }}>Enrollment Activity</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>Monthly enrollments</div>
              </div>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="enrollGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false}/>
                    <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CUSTOM_TOOLTIP/>}/>
                    <Area type="monotone" dataKey="enrollments" name="Enrollments" stroke="var(--accent)" fill="url(#enrollGrad)" strokeWidth={2}/>
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:13 }}>
                  No enrollment data yet
                </div>
              )}
            </div>
          )}

          {CAN.seePayments(role) && (
            <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'20px 24px' }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:14 }}>Revenue by Flow</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
                  {payments ? `RWF ${Number(payments.total_revenue || 0).toLocaleString()} total` : 'Loading…'}
                </div>
              </div>
              {revenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={revenueChartData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false}/>
                    <XAxis dataKey="flow" tick={{ fill:'var(--text-muted)', fontSize:10 }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CUSTOM_TOOLTIP/>} cursor={{ fill:'var(--bg-hover)' }}/>
                    <Bar dataKey="amount" name="Amount (RWF)" fill="var(--green)" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:13 }}>
                  No transaction data yet
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active Programs list */}
      {CAN.seePrograms(role) && programs.length > 0 && (
        <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>Active Programs</span>
            <a href="/programs" style={{ fontSize:11, color:'var(--accent)' }}>Manage →</a>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:1 }}>
            {programs.filter(p => p.status === 'ongoing' || p.status === 'active').slice(0,6).map(p => (
              <div key={p.id} style={{ padding:'14px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', flexDirection:'column', gap:6 }}>
                <div style={{ fontSize:13, fontWeight:600 }}>{p.title}</div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:20, background:'var(--accent)18', color:'var(--accent)', border:'1px solid var(--accent)33', textTransform:'capitalize' }}>
                    {p.program_type || p.type}
                  </span>
                  <span style={{ fontSize:10, color:'var(--text-muted)' }}>{p.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent items */}
      <div style={{ display:'grid', gridTemplateColumns: CAN.seeInbox(role) ? '1fr 1fr' : '1fr', gap:20 }}>

        {CAN.seeEnrollments(role) && (
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>Recent Enrollments</span>
              <a href="/enrollments" style={{ fontSize:11, color:'var(--accent)' }}>View all →</a>
            </div>
            {enrollments.length === 0
              ? <div style={{ padding:30, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No enrollments yet</div>
              : enrollments.map(e => (
                <div key={e.id} style={{ padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.full_name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{e.program_title || '—'}</div>
                  </div>
                  <Badge status={e.status}/>
                </div>
              ))
            }
          </div>
        )}

        {CAN.seeInbox(role) && (
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>
                Inbox
                {pendingMessages > 0 && <span style={{ marginLeft:8, fontSize:10, padding:'2px 7px', borderRadius:20, background:'rgba(240,96,96,0.15)', color:'var(--red-light)', border:'1px solid rgba(240,96,96,0.3)' }}>{pendingMessages} new</span>}
              </span>
              <a href="/inbox" style={{ fontSize:11, color:'var(--accent)' }}>View all →</a>
            </div>
            {messages.length === 0
              ? <div style={{ padding:30, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No messages yet</div>
              : messages.map(m => (
                <div key={m.id} style={{ padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.full_name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.subject || m.message?.slice(0,40)}</div>
                  </div>
                  <Badge status={m.status || 'new'}/>
                </div>
              ))
            }
          </div>
        )}

        {CAN.seeContent(role) && !CAN.seeInbox(role) && (
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>Recent Articles</span>
              <a href="/articles" style={{ fontSize:11, color:'var(--accent)' }}>View all →</a>
            </div>
            {articles.length === 0
              ? <div style={{ padding:30, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No articles yet</div>
              : articles.map(a => (
                <div key={a.id} style={{ padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.title}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{a.author_name || 'Unknown'}</div>
                  </div>
                  <Badge status={a.status}/>
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* Articles + Applications for admins */}
      {CAN.seeInbox(role) && CAN.seeContent(role) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>Recent Articles</span>
              <a href="/articles" style={{ fontSize:11, color:'var(--accent)' }}>View all →</a>
            </div>
            {articles.length === 0
              ? <div style={{ padding:30, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No articles yet</div>
              : articles.map(a => (
                <div key={a.id} style={{ padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.title}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{a.author_name || 'Unknown'}</div>
                  </div>
                  <Badge status={a.status}/>
                </div>
              ))
            }
          </div>

          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>
                Applications
                {pendingApps > 0 && <span style={{ marginLeft:8, fontSize:10, padding:'2px 7px', borderRadius:20, background:'rgba(232,164,58,0.15)', color:'var(--accent)', border:'1px solid rgba(232,164,58,0.3)' }}>{pendingApps} pending</span>}
              </span>
              <a href="/inbox" style={{ fontSize:11, color:'var(--accent)' }}>View all →</a>
            </div>
            {applications.length === 0
              ? <div style={{ padding:30, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No applications yet</div>
              : applications.map(a => (
                <div key={a.id} style={{ padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.full_name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, textTransform:'capitalize' }}>{a.type?.replace('_',' ')}</div>
                  </div>
                  <Badge status={a.status}/>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* Ecosystem Pillars */}
      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'20px 24px' }}>
        <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:14, marginBottom:18 }}>Ecosystem Pillars</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12 }}>
          {[
            { label:'Education & Training',  icon:'◐', color:'var(--blue)',   count: activePrograms },
            { label:'Services & Consulting', icon:'◉', color:'var(--accent)', count: null },
            { label:'Products & Platforms',  icon:'◈', color:'var(--green)',  count: null },
            { label:'Media & Communication', icon:'◎', color:'var(--amber)',  count: null },
            { label:'Innovation & Research', icon:'⬡', color:'var(--blue)',   count: null },
          ].map(p => (
            <div key={p.label} style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:14, textAlign:'center', border:'1px solid var(--border)' }}>
              <div style={{ fontSize:22, marginBottom:6, color:p.color }}>{p.icon}</div>
              <div style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:1.4 }}>{p.label}</div>
              {p.count !== null && <div style={{ fontSize:13, fontWeight:700, color:p.color, marginTop:6 }}>{p.count}</div>}
              <div style={{ marginTop:6 }}><Badge status="active"/></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}